/**
 * Vitrine publique — /demonstration
 * Page 100 % isolée (aucune connexion requise) pour présenter le simulateur
 * à la direction : arguments clés + les 5 appels modèles jouables.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { PhoneCall, HeartPulse, Bot, ClipboardCheck, FileText, GraduationCap, Play, Clock, ShieldAlert, ChevronRight, MonitorPlay } from 'lucide-react';
import DemoCall from '@/components/health/DemoCall';
import { DEMO_CALLS } from '@/components/health/demoCallScripts';
import { API } from '@/App';

const STATS = [
  { icon: HeartPulse, value: '32', label: 'protocoles MPDS interactifs (déterminants Santé)' },
  { icon: Bot, value: 'IA', label: 'citoyens virtuels réalistes — simulation vocale et texte' },
  { icon: PhoneCall, value: '3', label: 'modes par protocole : Guidé, Simulation, Démonstration' },
  { icon: ClipboardCheck, value: '100 %', label: 'évaluation automatisée avec rétroaction pédagogique' },
  { icon: FileText, value: 'RAO', label: 'répartition assistée par ordinateur, comme en centrale' },
  { icon: GraduationCap, value: '911', label: 'conçu pour la formation des répartiteurs d\'urgence' },
];

const DEMO_META = {
  douleur_thoracique: { tag: 'Cardiaque', color: '#DC2626' },
  reaction_allergique: { tag: 'Anaphylaxie', color: '#F59E0B' },
  inconscience: { tag: 'ACR / RCR guidée', color: '#7C3AED' },
  avc_suspecte: { tag: 'AVC — échelle VITE', color: '#2E6CF5' },
  overdose_empoisonnement: { tag: 'Overdose', color: '#059669' },
};

export default function DemoShowcase() {
  const [protocols, setProtocols] = useState(null);
  const [active, setActive] = useState(null);
  const [presentation, setPresentation] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/demo-protocols`)
      .then(r => setProtocols(r.data))
      .catch(() => setError(true));
  }, []);

  const startPresentation = () => {
    setPresentation(0);
    document.documentElement.requestFullscreen?.().catch(() => {});
  };
  const stopPresentation = () => {
    setPresentation(null);
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
  };

  if (presentation != null && protocols?.length) {
    const n = protocols.length;
    const p = protocols[presentation % n];
    return (
      <div className="relative" data-testid="presentation-mode">
        <DemoCall
          key={`${p.id}-${presentation}`}
          protocol={p}
          standalone
          presentation
          onComplete={() => setPresentation(i => i + 1)}
          onExit={stopPresentation}
        />
        <div className="fixed bottom-4 left-4 z-50 px-3.5 py-2 rounded-full text-white text-xs font-bold shadow-lg flex items-center gap-2" style={{ backgroundColor: '#EE2130' }} data-testid="presentation-counter">
          <MonitorPlay className="w-3.5 h-3.5" /> Présentation en boucle — appel {(presentation % n) + 1}/{n}
        </div>
      </div>
    );
  }

  if (active) {
    return <DemoCall protocol={active} standalone onExit={() => setActive(null)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="demo-showcase-page">
      {/* Hero */}
      <header className="relative overflow-hidden" style={{ backgroundColor: '#002768' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #2E6CF5 0, transparent 45%), radial-gradient(circle at 80% 70%, #EE2130 0, transparent 40%)' }} />
        <div className="relative max-w-5xl mx-auto px-6 py-12 sm:py-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#EE2130' }}>
              <PhoneCall className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-lg leading-tight">Simulateur 911</p>
              <p className="text-blue-200 text-xs">Collège Montmorency — Techniques de répartition d'urgence</p>
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white max-w-3xl leading-tight">
            Former les répartiteurs 911 de demain, <span style={{ color: '#8AB4FF' }}>un appel à la fois.</span>
          </h1>
          <p className="mt-4 text-blue-100 text-base max-w-2xl">
            Une plateforme pédagogique complète où les étudiantes et étudiants prennent de vrais appels simulés :
            citoyens virtuels propulsés par l'IA, protocoles médicaux MPDS interactifs, notes horodatées,
            priorisation en temps réel et rétroaction automatisée.
          </p>
          <button
            onClick={startPresentation}
            disabled={!protocols?.length}
            className="mt-6 inline-flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-bold text-white shadow-lg transition-all hover:brightness-110 hover:scale-[1.02] disabled:opacity-50"
            style={{ backgroundColor: '#EE2130' }}
            data-testid="start-presentation-btn"
          >
            <MonitorPlay className="w-5 h-5" />
            Mode présentation — les 5 appels en boucle, plein écran
          </button>
          <p className="mt-2 text-blue-300 text-[11px]">Idéal à projeter : lecture automatique continue, évaluation affichée à la fin de chaque appel. Quittez avec la flèche ← ou Échap.</p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {STATS.map((s) => (
              <div key={s.label} className="rounded-xl p-3.5 border border-white/15 bg-white/5 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-1">
                  <s.icon className="w-4 h-4" style={{ color: '#8AB4FF' }} />
                  <span className="text-white font-extrabold text-lg">{s.value}</span>
                </div>
                <p className="text-blue-200 text-[11px] leading-snug">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Demos */}
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-6">
          <h2 className="text-lg font-bold" style={{ color: '#002768' }}>Regardez une prise d'appel modèle</h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Chaque démonstration rejoue un appel complet, exactement comme le vit l'étudiant : transfert du 911 primaire,
            protocole qui se remplit phase par phase, drapeaux rouges, priorisation, envoi des ambulances et quiz éclair.
            Durée : 3 à 4 minutes (accélérable à 2x).
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-600 py-8 text-center" data-testid="showcase-error">
            Impossible de charger les démonstrations pour le moment. Réessayez dans quelques instants.
          </p>
        )}
        {!protocols && !error && (
          <p className="text-sm text-gray-400 py-8 text-center animate-pulse">Chargement des démonstrations…</p>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          {(protocols || []).map((p) => {
            const demo = DEMO_CALLS[p.id];
            const meta = DEMO_META[p.id] || { tag: 'Santé', color: '#002768' };
            if (!demo) return null;
            return (
              <button
                key={p.id}
                onClick={() => setActive(p)}
                className="group text-left rounded-2xl border-2 border-gray-100 bg-white p-5 hover:border-blue-300 hover:shadow-lg transition-all"
                data-testid={`showcase-demo-${p.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ backgroundColor: `${meta.color}18`, color: meta.color }}>
                    {meta.tag}
                  </span>
                  <span className="text-[11px] text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" /> 3-4 min</span>
                </div>
                <h3 className="font-bold text-gray-900">{p.title}</h3>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{demo.title} — {demo.caller}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white px-3.5 py-2 rounded-full transition-colors" style={{ backgroundColor: '#002768' }}>
                    <Play className="w-3.5 h-3.5" /> Lancer la démonstration
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Argumentaire */}
        <section className="mt-10 rounded-2xl border-2 border-gray-100 bg-white p-6">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#EE2130' }} />
            <div>
              <h3 className="font-bold" style={{ color: '#002768' }}>Pourquoi ce simulateur?</h3>
              <ul className="mt-2 space-y-1.5 text-sm text-gray-600">
                <li>• <strong>Pratique illimitée et sécuritaire</strong> : les étudiants s'exercent sur des appels critiques (arrêt cardiaque, AVC, anaphylaxie) sans risque, autant de fois que nécessaire.</li>
                <li>• <strong>Réalisme québécois</strong> : transfert du 911 primaire, priorisation P1-P4, envoi d'ambulances, notes d'appel horodatées — comme en centrale.</li>
                <li>• <strong>Rétroaction immédiate</strong> : chaque appel est évalué automatiquement et génère un rapport imprimable, phase par phase.</li>
                <li>• <strong>Suivi pédagogique complet</strong> : tableaux de bord enseignant, examens, prise de rendez-vous d'évaluation, guides mobiles par code QR.</li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <footer className="py-6 text-center text-xs text-gray-400">
        Simulateur 911 — Collège Montmorency · Démonstration publique · Aucune donnée étudiante n'est accessible depuis cette page
      </footer>
    </div>
  );
}
